import React, { useState, useEffect, useMemo, useRef } from 'react';
import './ServiceProviderManagement.css';
import './mobile.css';
import { addProvider, getAllProviders } from '../../utils/firebase';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour = hours % 12 || 12;
  return `${hour}:${minutes} ${period}`;
};

const TimeSelector = ({ value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeOptions = useMemo(() => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 30) {
        const hour = i.toString().padStart(2, '0');
        const minute = j.toString().padStart(2, '0');
        const timeString = `${hour}:${minute}`;
        const formattedTime = formatTime(timeString);
        times.push({ value: timeString, label: formattedTime });
      }
    }
    return times;
  }, []);

  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedElement = listRef.current.querySelector('.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="time-selector" ref={dropdownRef}>
      <div 
        className={`time-display ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="far fa-clock"></i>
        <span className="time-text">{formatTime(value)}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>
      
      {isOpen && (
        <div className="time-dropdown">
          <div className="time-list" ref={listRef}>
            {timeOptions.map((time) => (
              <div
                key={time.value}
                className={`time-option ${time.value === value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(time.value);
                  setIsOpen(false);
                }}
              >
                {time.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ServiceProviderManagement = () => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    phoneNumber: '',
    email: '',
    address: '',
    location: '',
    description: '',
    rating: '0',
    isValidated: false
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [providers, setProviders] = useState([]);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [tempCategory, setTempCategory] = useState('');
  const [businessHours, setBusinessHours] = useState({
    monday: { isOpen: false, open: '09:00', close: '17:00' },
    tuesday: { isOpen: false, open: '09:00', close: '17:00' },
    wednesday: { isOpen: false, open: '09:00', close: '17:00' },
    thursday: { isOpen: false, open: '09:00', close: '17:00' },
    friday: { isOpen: false, open: '09:00', close: '17:00' },
    saturday: { isOpen: false, open: '09:00', close: '17:00' },
    sunday: { isOpen: false, open: '09:00', close: '17:00' }
  });

  // Add days of week array for mapping
  const daysOfWeek = [
    { id: 'monday', label: 'Monday' },
    { id: 'tuesday', label: 'Tuesday' },
    { id: 'wednesday', label: 'Wednesday' },
    { id: 'thursday', label: 'Thursday' },
    { id: 'friday', label: 'Friday' },
    { id: 'saturday', label: 'Saturday' },
    { id: 'sunday', label: 'Sunday' }
  ];

  // Add business hours handler
  const handleHoursChange = (day, field, value) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  // Modify fetchCategoriesAndProviders to get categories from both sources
  useEffect(() => {
    const fetchCategoriesAndProviders = async () => {
      try {
        // Fetch from categories collection
        const categoriesSnapshot = await getDocs(collection(db, 'categories'));
        const categoriesFromCollection = categoriesSnapshot.docs.map(doc => doc.data().name);

        // Fetch from providers
        const providersSnapshot = await getDocs(collection(db, 'providers'));
        const categoriesFromProviders = [...new Set(
          providersSnapshot.docs.map(doc => doc.data().category)
        )].filter(Boolean);

        // Combine both sets of categories and remove duplicates
        const allCategories = [...new Set([...categoriesFromCollection, ...categoriesFromProviders])]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        setCategories(allCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategoriesAndProviders();
  }, []);

  // Add this useEffect to check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        console.log('Logged in user:', user.email);
        const token = await user.getIdTokenResult(true);
        console.log('Admin status:', {
          email: user.email,
          claims: token.claims
        });
      } else {
        console.log('No user logged in');
      }
    };
    
    checkAdminStatus();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category => 
    category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCategorySearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    
    // Check if the entered value exactly matches any existing category
    const matchingCategory = categories.find(
      category => category.toLowerCase() === searchValue.toLowerCase()
    );

    if (matchingCategory) {
      // If there's a match, update the form data with the existing category
      setFormData(prev => ({ ...prev, category: matchingCategory }));
      setIsNewCategory(false);
    } else {
      // If no match, set temporary category and mark as new
      setTempCategory(searchValue);
      setIsNewCategory(true);
    }
  };

  const addNewCategory = async (categoryName) => {
    try {
      // Check if user is logged in
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to add a category');
      }

      // Check if user has admin email
      if (user.email !== 'admin@aroundu.co.za') {
        throw new Error('Only admin users can add categories');
      }

      // Check if category name is valid
      if (!categoryName || categoryName.trim().length === 0) {
        throw new Error('Category name cannot be empty');
      }

      // Check if category already exists
      const categoryExists = categories.some(
        cat => cat.toLowerCase() === categoryName.trim().toLowerCase()
      );
      if (categoryExists) {
        throw new Error('Category already exists');
      }

      // Add to categories collection with proper metadata
      const newCategoryData = {
        name: categoryName.trim(),
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
        updatedAt: new Date().toISOString(),
        updatedBy: user.uid
      };

      console.log('Adding new category:', newCategoryData); // Debug log

      const docRef = await addDoc(collection(db, 'categories'), newCategoryData);
      
      // Update local categories state
      const updatedCategories = [...categories, categoryName.trim()].sort();
      setCategories(updatedCategories);
      setIsNewCategory(false);
      
      console.log('Category added successfully:', docRef.id); // Debug log
      return true;

    } catch (error) {
      console.error('Detailed error adding category:', error); // Debug log
      setMessage({ 
        text: 'Error adding category: ' + error.message, 
        type: 'error' 
      });
      return false;
    }
  };

  const handleCategoryKeyPress = async (e) => {
    if (e.key === 'Enter' && isNewCategory && tempCategory) {
      e.preventDefault(); // Prevent form submission
      const success = await addNewCategory(tempCategory);
      if (success) {
        setFormData(prev => ({ ...prev, category: tempCategory }));
        setMessage({ text: 'New category added successfully!', type: 'success' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check if user is logged in and is admin
      const user = auth.currentUser;
      if (!user) {
        setMessage({ text: 'You must be logged in to add a provider', type: 'error' });
        return;
      }

      if (user.email !== 'admin@aroundu.co.za') {
        setMessage({ text: 'Insufficient permissions: Admin access required', type: 'error' });
        return;
      }

      // If it's a new category, add it first
      if (isNewCategory && tempCategory) {
        const categoryAdded = await addNewCategory(tempCategory);
        if (!categoryAdded) {
          setMessage({ text: 'Error adding new category', type: 'error' });
          return;
        }
        // Update the form data with the new category
        setFormData(prev => ({ ...prev, category: tempCategory }));
      }

      const providerData = {
        ...formData,
        businessHours,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addProvider(providerData);
      setMessage({ text: 'Service provider added successfully!', type: 'success' });
      
      // Reset form
      setFormData({
        name: '',
        category: '',
        phoneNumber: '',
        email: '',
        address: '',
        location: '',
        description: '',
        rating: '0',
        isValidated: false
      });
      
      // Reset business hours
      setBusinessHours({
        monday: { isOpen: false, open: '09:00', close: '17:00' },
        tuesday: { isOpen: false, open: '09:00', close: '17:00' },
        wednesday: { isOpen: false, open: '09:00', close: '17:00' },
        thursday: { isOpen: false, open: '09:00', close: '17:00' },
        friday: { isOpen: false, open: '09:00', close: '17:00' },
        saturday: { isOpen: false, open: '09:00', close: '17:00' },
        sunday: { isOpen: false, open: '09:00', close: '17:00' }
      });
    } catch (error) {
      setMessage({ text: 'Error adding provider: ' + error.message, type: 'error' });
    }
  };

  // Add this at the top with other constants
  const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="provider-management">
      <h2>Add New Service Provider</h2>
      <form className="provider-form" onSubmit={handleSubmit}>
        {/* Existing name field */}
        <div className="form-group">
          <label htmlFor="name">Business Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        {/* Updated category field with search */}
        <div className="form-group">
          <label htmlFor="category">Category *</label>
          <div className="category-search">
            <input
              type="text"
              placeholder="Search or create new category..."
              value={searchTerm}
              onChange={handleCategorySearch}
              onKeyPress={handleCategoryKeyPress}
              className="category-search-input"
            />
            {isNewCategory && searchTerm && (
              <div className="new-category-notice">
                Press Enter to create new category: "{searchTerm}"
              </div>
            )}
          </div>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          >
            <option value="">Select a category</option>
            {filteredCategories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
            {isNewCategory && searchTerm && (
              <option value={searchTerm}>
                {searchTerm} (New)
              </option>
            )}
          </select>
        </div>

        {/* New rating field */}
        <div className="form-group">
          <label htmlFor="rating">Rating</label>
          <select
            id="rating"
            name="rating"
            value={formData.rating}
            onChange={handleChange}
          >
            <option value="0">Select rating</option>
            <option value="1">1 Star</option>
            <option value="2">2 Stars</option>
            <option value="3">3 Stars</option>
            <option value="4">4 Stars</option>
            <option value="5">5 Stars</option>
          </select>
        </div>

        {/* New validation checkbox */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isValidated"
              checked={formData.isValidated}
              onChange={handleChange}
            />
            Business Validated
          </label>
        </div>

        {/* Existing fields */}
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number *</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">Address</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="location">Location/Area</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="e.g., North Side, South Side, Downtown"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
          />
        </div>

        {/* Add Business Hours Section */}
        <div className="form-group business-hours-group">
          <h3>Business Hours</h3>
          <div className="business-hours-container">
            {daysOrder.map(day => (
              <div key={day} className="day-row">
                <div className="day-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={businessHours[day]?.isOpen || false}
                      onChange={(e) => handleHoursChange(day, 'isOpen', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                </div>
                
                {businessHours[day]?.isOpen && (
                  <div className="hours-inputs">
                    <div className="time-input-group">
                      <label>Opens</label>
                      <TimeSelector
                        value={businessHours[day]?.open || '09:00'}
                        onChange={(time) => handleHoursChange(day, 'open', time)}
                      />
                    </div>
                    <span className="time-separator">to</span>
                    <div className="time-input-group">
                      <label>Closes</label>
                      <TimeSelector
                        value={businessHours[day]?.close || '17:00'}
                        onChange={(time) => handleHoursChange(day, 'close', time)}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <button type="submit">Add Service Provider</button>
      </form>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default ServiceProviderManagement; 